import type { BrokerIntegration } from './autoResumeLatch';

/**
 * Per-integration relay ownership across browser tabs (2026-09-13, decision D1: **the last tab to
 * ask to connect an integration is the one that gets connected, if its logon succeeds**).
 *
 * The broker socket is browser-owned (the tab holds the credentials and the WebSocket), but the BE
 * session is per USER: `RithmicOpenSocket` / `CqgOpenSocket` are pushed to every tab, and every tab
 * used to open its own socket and log in — two tabs then fought over one broker login (Rithmic
 * `rp_code 13` + `1011 permission denied` on one of them ~20×/min; CQG `LoggedOff FORCED "concurrent
 * session"` and a "Too many failed" lockout — prod 2026-09-12). CQG had a log-only probe; Rithmic had
 * nothing.
 *
 * Protocol (one `BroadcastChannel` per integration, `optimus-relay:<integration>`):
 *   - `claim{tabId, seq}`  — this tab is about to connect (a user Connect or an auto-resume). Every
 *     other tab goes PASSIVE; the current owner additionally YIELDS: it closes its socket cleanly
 *     (1000), tells the BE (`…RelayClosed`), then broadcasts `released`. The claimant waits for that
 *     `released` (bounded) before it POSTs `/connect`, so the BE sees the old socket go before the new
 *     session is asked for.
 *   - `connected{tabId}`   — the claimant's logon was accepted: it is the owner and takes the lease.
 *   - `failed{tabId}`      — the claimant's logon was refused: it drops the claim; the tab it displaced
 *     is told it may RECLAIM once (no loop — "the last tab wins if its connection succeeds").
 *   - `released{tabId}`    — the owner let go (yield, Disconnect, tab close): passive tabs learn the
 *     owner is gone and may resume once (subject to the D4 latch).
 *   - `probe{tabId}`       — a fresh tab asks who owns it; the owner re-announces `connected`.
 *
 * Lease: the owner holds an exclusive Web Lock (`optimus-relay-owner:<integration>`) while it owns
 * the integration. A lock dies with its tab, so `isOwnerAlive()` (via `navigator.locks.query`) tells a
 * passive tab whether the owner it last heard from is still around when a `released` never came (a
 * crashed tab). Without Web Locks the channel alone still elects one owner; liveness is then assumed.
 *
 * Passive tabs never open a broker socket (`mayOpenSocket()`), never auto-resume, and render the BE
 * state as "connected in another tab". Deliberately integration-keyed so a future broker plugs in.
 */
export type OwnershipMode = 'idle' | 'claiming' | 'owner' | 'passive';

export interface OwnershipSnapshot {
  readonly tabId: string;
  readonly mode: OwnershipMode;
  readonly ownerTabId: string | null;
}

export type OwnershipEvent =
  /** We held (or were claiming) the integration and another tab claimed it: close our socket, then `release()`. */
  | { type: 'yield'; toTabId: string }
  /** The tab that displaced us failed its logon: we may reconnect once. */
  | { type: 'reclaim'; fromTabId: string }
  /** The owner released (yield to nobody, Disconnect, tab close) and no new owner has appeared. */
  | { type: 'owner-gone'; tabId: string }
  /** Mode or owner changed (for badges / dialogs). */
  | { type: 'change'; snapshot: OwnershipSnapshot };

export type OwnershipWireMessage =
  | { type: 'claim'; tabId: string; seq: number }
  | { type: 'released'; tabId: string }
  | { type: 'connected'; tabId: string }
  | { type: 'failed'; tabId: string }
  | { type: 'probe'; tabId: string };

/** The subset of `BroadcastChannel` the protocol needs (fakeable in tests). */
export interface OwnershipChannel {
  postMessage(message: OwnershipWireMessage): void;
  onmessage: ((event: { data: OwnershipWireMessage }) => void) | null;
  close(): void;
}

/** The subset of `LockManager` the lease needs (fakeable in tests). */
export interface OwnershipLocks {
  request(
    name: string,
    options: { mode: 'exclusive'; ifAvailable: true },
    callback: (lock: unknown | null) => Promise<void>,
  ): Promise<unknown>;
  query(): Promise<{ held?: ReadonlyArray<{ name?: string | null }> | null }>;
}

export interface RelayOwnershipDeps {
  /** `null` = no channel (single-tab fallback); omit = the real `BroadcastChannel` when available. */
  channel?: OwnershipChannel | null;
  /** `null` = no Web Locks (channel-only election); omit = `navigator.locks` when available. */
  locks?: OwnershipLocks | null;
  tabId?: string;
  /** How long `claim()` waits for the current owner's `released` before proceeding anyway. */
  claimWaitMs?: number;
}

export function ownershipChannelName(integration: BrokerIntegration): string {
  return `optimus-relay:${integration}`;
}

export function ownershipLeaseName(integration: BrokerIntegration): string {
  return `optimus-relay-owner:${integration}`;
}

function newTabId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID().slice(0, 8);
  } catch { /* fall through */ }
  return Math.random().toString(36).slice(2, 10);
}

function defaultChannel(integration: BrokerIntegration): OwnershipChannel | null {
  try {
    if (typeof BroadcastChannel === 'undefined') return null;
    return new BroadcastChannel(ownershipChannelName(integration)) as unknown as OwnershipChannel;
  } catch {
    return null;
  }
}

function defaultLocks(): OwnershipLocks | null {
  try {
    const locks = (typeof navigator !== 'undefined' ? (navigator as { locks?: unknown }).locks : undefined) as OwnershipLocks | undefined;
    return locks && typeof locks.request === 'function' && typeof locks.query === 'function' ? locks : null;
  } catch {
    return null;
  }
}

export class RelayOwnership {
  readonly tabId: string;
  private mode: OwnershipMode = 'idle';
  private ownerTabId: string | null = null;
  private seq = 0;
  /** The tab whose claim displaced us while we owned / were claiming — the one whose `failed` lets us reclaim. */
  private displacedBy: string | null = null;
  /** We announced a claim/connected that other tabs saw and still need a `released` for. */
  private announced = false;
  private pendingClaim: { ownerTabId: string; resolve: () => void; timer: ReturnType<typeof setTimeout> } | null = null;
  private leaseRelease: (() => void) | null = null;
  private readonly listeners = new Set<(event: OwnershipEvent) => void>();
  private readonly channel: OwnershipChannel | null;
  private readonly locks: OwnershipLocks | null;
  private readonly claimWaitMs: number;

  constructor(readonly integration: BrokerIntegration, deps: RelayOwnershipDeps = {}) {
    this.tabId = deps.tabId ?? newTabId();
    this.channel = deps.channel === undefined ? defaultChannel(integration) : deps.channel;
    this.locks = deps.locks === undefined ? defaultLocks() : deps.locks;
    this.claimWaitMs = deps.claimWaitMs ?? 2_000;
    if (this.channel) {
      this.channel.onmessage = (event) => this.onWire(event.data);
      this.post({ type: 'probe', tabId: this.tabId });
    }
  }

  snapshot(): OwnershipSnapshot {
    return { tabId: this.tabId, mode: this.mode, ownerTabId: this.ownerTabId };
  }

  /** Another tab holds (or is taking) the integration: this tab must not open a socket or auto-resume. */
  isPassive(): boolean { return this.mode === 'passive'; }

  /** Whether a broker socket may be opened from this tab right now. */
  mayOpenSocket(): boolean { return this.mode !== 'passive'; }

  onEvent(listener: (event: OwnershipEvent) => void): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  /**
   * This tab is about to connect. Broadcasts the claim and, when another tab is known to own the
   * integration, waits (bounded by `claimWaitMs`) for its `released` so the BE sees the old socket
   * close before the new `/connect` lands.
   */
  claim(): Promise<void> {
    this.seq += 1;
    this.displacedBy = null;
    const previousOwner = this.ownerTabId;
    this.mode = 'claiming';
    this.announced = true;
    this.post({ type: 'claim', tabId: this.tabId, seq: this.seq });
    this.emitChange();
    if (!previousOwner || previousOwner === this.tabId || !this.channel) return Promise.resolve();
    this.settlePendingClaim();
    return new Promise<void>((resolve) => {
      const timer = setTimeout(() => { this.pendingClaim = null; resolve(); }, this.claimWaitMs);
      this.pendingClaim = { ownerTabId: previousOwner, resolve, timer };
    });
  }

  /** Our logon was accepted: we own the integration now. */
  connected(): void {
    this.mode = 'owner';
    this.ownerTabId = this.tabId;
    this.displacedBy = null;
    this.announced = true;
    this.post({ type: 'connected', tabId: this.tabId });
    void this.acquireLease();
    this.emitChange();
  }

  /** Our logon was refused: drop the claim so the tab we displaced may reclaim once. */
  failed(): void {
    if (this.mode !== 'claiming' && this.mode !== 'owner') return;
    this.releaseLease();
    if (this.ownerTabId === this.tabId) this.ownerTabId = null;
    this.mode = this.ownerTabId ? 'passive' : 'idle';
    this.announced = false;
    this.post({ type: 'failed', tabId: this.tabId });
    this.emitChange();
  }

  /** We let go (yielded to another tab, user Disconnect, tab close, kick). */
  release(): void {
    this.releaseLease();
    if (this.ownerTabId === this.tabId) this.ownerTabId = null;
    const wasAnnounced = this.announced;
    this.announced = false;
    this.mode = this.ownerTabId ? 'passive' : 'idle';
    if (wasAnnounced) this.post({ type: 'released', tabId: this.tabId });
    this.emitChange();
  }

  /** Whether the owner we last heard from still holds its lease (true when Web Locks are unavailable). */
  async isOwnerAlive(): Promise<boolean> {
    if (!this.ownerTabId) return false;
    if (this.ownerTabId === this.tabId) return true;
    if (!this.locks) return true;
    try {
      const state = await this.locks.query();
      return state.held?.some((l) => l.name === ownershipLeaseName(this.integration)) ?? true;
    } catch {
      return true;
    }
  }

  close(): void {
    this.settlePendingClaim();
    this.releaseLease();
    try { this.channel?.close(); } catch { /* ignore */ }
    this.listeners.clear();
  }

  // ── wire ────────────────────────────────────────────────────────────────────────────────────────

  private onWire(message: OwnershipWireMessage): void {
    if (!message || typeof message !== 'object' || message.tabId === this.tabId) return;
    switch (message.type) {
      case 'claim': {
        const wasHolding = this.mode === 'owner' || this.mode === 'claiming';
        this.ownerTabId = message.tabId;
        if (wasHolding) {
          // Last claim wins: hand over. The relay closes its socket, then calls release().
          this.displacedBy = message.tabId;
          this.releaseLease();
          this.mode = 'passive';
          this.emit({ type: 'yield', toTabId: message.tabId });
        } else {
          this.mode = 'passive';
        }
        this.emitChange();
        return;
      }
      case 'connected': {
        this.ownerTabId = message.tabId;
        if (this.mode === 'owner' || this.mode === 'claiming') {
          // Someone else won without us seeing their claim (a missed message): yield anyway.
          this.displacedBy = message.tabId;
          this.releaseLease();
          this.mode = 'passive';
          this.emit({ type: 'yield', toTabId: message.tabId });
        } else {
          this.mode = 'passive';
        }
        this.emitChange();
        return;
      }
      case 'released':
      case 'failed': {
        if (this.pendingClaim && this.pendingClaim.ownerTabId === message.tabId) this.settlePendingClaim(true);
        if (this.ownerTabId === message.tabId) this.ownerTabId = null;
        if (this.mode === 'passive' && !this.ownerTabId) this.mode = 'idle';
        if (message.type === 'failed' && this.displacedBy === message.tabId) {
          this.displacedBy = null;
          this.emit({ type: 'reclaim', fromTabId: message.tabId });
        } else if (message.type === 'released' && !this.ownerTabId && this.mode === 'idle') {
          this.emit({ type: 'owner-gone', tabId: message.tabId });
        }
        this.emitChange();
        return;
      }
      case 'probe': {
        if (this.mode === 'owner') this.post({ type: 'connected', tabId: this.tabId });
        return;
      }
      default:
        return;
    }
  }

  private post(message: OwnershipWireMessage): void {
    try { this.channel?.postMessage(message); } catch { /* channel closed / unsupported */ }
  }

  private settlePendingClaim(resolve = false): void {
    const pending = this.pendingClaim;
    if (!pending) return;
    clearTimeout(pending.timer);
    this.pendingClaim = null;
    if (resolve) pending.resolve();
  }

  private emit(event: OwnershipEvent): void {
    for (const l of Array.from(this.listeners)) {
      try { l(event); } catch { /* a listener must not break the protocol */ }
    }
  }

  private emitChange(): void {
    this.emit({ type: 'change', snapshot: this.snapshot() });
  }

  // ── lease ───────────────────────────────────────────────────────────────────────────────────────

  private async acquireLease(): Promise<void> {
    if (!this.locks || this.leaseRelease) return;
    const name = ownershipLeaseName(this.integration);
    // The displaced owner releases its lease on the `claim` message, well before our logon completes;
    // a few short retries cover a slow tab.
    for (let attempt = 0; attempt < 4 && this.mode === 'owner' && !this.leaseRelease; attempt++) {
      let granted = false;
      try {
        await this.locks.request(name, { mode: 'exclusive', ifAvailable: true }, (lock) => {
          if (!lock || this.mode !== 'owner') return Promise.resolve();
          granted = true;
          return new Promise<void>((resolve) => { this.leaseRelease = resolve; });
        });
      } catch {
        return;
      }
      if (granted || this.leaseRelease) return;
      await new Promise((r) => setTimeout(r, 250));
    }
  }

  private releaseLease(): void {
    const release = this.leaseRelease;
    this.leaseRelease = null;
    release?.();
  }
}

// ── app-level singletons (one per integration) ───────────────────────────────────────────────────
const _instances = new Map<BrokerIntegration, RelayOwnership>();

/** The shared ownership tracker for an integration (lazily created). */
export function getRelayOwnership(integration: BrokerIntegration): RelayOwnership {
  let o = _instances.get(integration);
  if (!o) { o = new RelayOwnership(integration); _instances.set(integration, o); }
  return o;
}

/** Test seam: replace / drop the singleton for an integration. */
export function _setRelayOwnershipForTests(integration: BrokerIntegration, ownership: RelayOwnership | null): void {
  if (ownership) _instances.set(integration, ownership);
  else _instances.delete(integration);
}
